import { cn } from "@/lib/utils";
import { X, Sword, Lock, Check, Sparkles } from "lucide-react";

// Base sword definitions with unlock requirements
const BASE_SWORDS = [
  { 
    id: 'bronze_sword', 
    name: 'Bronze Sword', 
    emoji: '🥉', 
    size: 3, 
    unlockLevel: 3,
    color: 'from-amber-600 to-amber-800',
    borderColor: 'border-amber-500',
    description: 'Clears a 3x3 area',
    rarity: 'common'
  },
  { 
    id: 'silver_sword', 
    name: 'Silver Sword', 
    emoji: '🥈', 
    size: 4, 
    unlockLevel: 20,
    color: 'from-gray-400 to-gray-600',
    borderColor: 'border-gray-400',
    description: 'Clears a 4x4 area',
    rarity: 'rare'
  },
  { 
    id: 'golden_sword', 
    name: 'Golden Sword', 
    emoji: '🥇', 
    size: 5, 
    unlockLevel: 50,
    color: 'from-yellow-500 to-amber-600',
    borderColor: 'border-yellow-400',
    description: 'Clears a 5x5 area',
    rarity: 'epic'
  },
  { 
    id: 'diamond_sword', 
    name: 'Diamond Sword', 
    emoji: '💎', 
    size: 7, 
    unlockLevel: 100,
    color: 'from-cyan-400 to-blue-600',
    borderColor: 'border-cyan-400',
    description: 'Clears a 7x7 area',
    rarity: 'legendary'
  },
  { 
    id: 'master_sword', 
    name: 'Master Sword', 
    emoji: '👑', 
    size: 8, 
    unlockLevel: 150,
    color: 'from-purple-500 to-violet-700',
    borderColor: 'border-purple-400',
    description: 'Clears an 8x8 area',
    rarity: 'legendary'
  },
];

// God Sword definition (special equipment)
const GOD_SWORD = {
  id: 'god_sword',
  name: 'God Sword',
  emoji: '⚔️',
  size: 'ALL',
  color: 'from-yellow-400 via-amber-500 to-orange-600',
  borderColor: 'border-yellow-300',
  description: 'Clears ENTIRE board!',
  rarity: 'legendary',
  isSpecial: true
};

export function SwordSelectionModal({ 
  isOpen, 
  onClose, 
  onSelectSword, 
  onSelectGodSword,
  onRechargeGodSword,
  currentLevel,
  equippedSword,
  swordUsesRemaining,
  maxSwordUses,
  // God sword props
  hasGodSword,
  godSwordUsesRemaining,
  disabled = false
}) {
  if (!isOpen) return null;

  const getUnlockedSwords = () => {
    return BASE_SWORDS.filter(sword => currentLevel >= sword.unlockLevel);
  };

  const unlockedSwords = getUnlockedSwords();
  const bestUnlockedSword = unlockedSwords[unlockedSwords.length - 1];

  const handleSwordClick = (sword) => {
    if (currentLevel >= sword.unlockLevel && !disabled && swordUsesRemaining > 0) {
      onSelectSword(sword.id);
      onClose();
    }
  };

  const handleGodSwordClick = () => {
    if (hasGodSword && godSwordUsesRemaining > 0 && !disabled) {
      onSelectGodSword();
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-md mx-auto">
        <div className="bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Sword className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Select Sword</h2>
                <p className="text-slate-400 text-sm">
                  Choose a sword to use
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
          
          {/* Sword List */}
          <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
            {/* God Sword - Show first if owned */}
            {hasGodSword && (
              <div className="relative">
                <button
                  onClick={godSwordUsesRemaining > 0 ? handleGodSwordClick : undefined}
                  disabled={godSwordUsesRemaining <= 0 || disabled}
                  className={cn(
                    "w-full p-4 rounded-xl border-2 transition-all duration-200",
                    "flex items-center gap-4 relative overflow-hidden",
                    godSwordUsesRemaining > 0 && !disabled
                      ? `bg-gradient-to-r ${GOD_SWORD.color} ${GOD_SWORD.borderColor} hover:scale-[1.02] active:scale-[0.98]`
                      : `bg-slate-800/50 border-slate-600`
                  )}
                  data-testid="sword-option-god_sword"
                >
                  {/* Sparkle effect for God Sword */}
                  {godSwordUsesRemaining > 0 && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                  )}
                  
                  {/* Sword Icon */}
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl bg-black/20 relative z-10">
                    {GOD_SWORD.emoji}
                  </div>
                  
                  {/* Sword Info */}
                  <div className="flex-1 text-left relative z-10">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "font-bold",
                        godSwordUsesRemaining > 0 ? "text-white" : "text-slate-400"
                      )}>{GOD_SWORD.name}</span>
                      <span className="px-2 py-0.5 bg-yellow-500/30 text-yellow-200 text-xs font-bold rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> SPECIAL
                      </span>
                    </div>
                    <p className={cn(
                      "text-sm",
                      godSwordUsesRemaining > 0 ? "text-white/80" : "text-slate-500"
                    )}>
                      {godSwordUsesRemaining > 0 ? GOD_SWORD.description : 'Depleted - needs recharge'}
                    </p>
                  </div>
                  
                  {/* Uses Badge or Recharge Button */}
                  {godSwordUsesRemaining > 0 ? (
                    <div className="px-3 py-1 rounded-lg text-sm font-bold relative z-10 bg-black/20 text-white">
                      {godSwordUsesRemaining}/5
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRechargeGodSword?.();
                        onClose();
                      }}
                      className="px-3 py-2 rounded-lg text-xs font-bold bg-green-500 hover:bg-green-600 text-white relative z-10 transition-colors"
                    >
                      Recharge
                    </button>
                  )}
                </button>
              </div>
            )}
            
            {/* Section Divider if God Sword shown */}
            {hasGodSword && (
              <div className="flex items-center gap-3 py-2">
                <div className="flex-1 h-px bg-slate-700" />
                <span className="text-slate-500 text-xs font-medium">STANDARD SWORDS</span>
                <div className="flex-1 h-px bg-slate-700" />
              </div>
            )}
            
            {/* Standard Swords */}
            {BASE_SWORDS.map((sword) => {
              const isUnlocked = currentLevel >= sword.unlockLevel;
              const isEquipped = equippedSword === sword.id;
              const isBest = sword.id === bestUnlockedSword?.id;
              const canUse = isUnlocked && swordUsesRemaining > 0 && !disabled;
              
              return (
                <button
                  key={sword.id}
                  onClick={() => handleSwordClick(sword)}
                  disabled={!canUse}
                  className={cn(
                    "w-full p-4 rounded-xl border-2 transition-all duration-200",
                    "flex items-center gap-4",
                    isUnlocked 
                      ? canUse
                        ? `bg-gradient-to-r ${sword.color} ${sword.borderColor} hover:scale-[1.02] active:scale-[0.98]`
                        : `bg-slate-800/50 ${sword.borderColor} opacity-50`
                      : "bg-slate-800/30 border-slate-700 opacity-50 cursor-not-allowed"
                  )}
                  data-testid={`sword-option-${sword.id}`}
                >
                  {/* Sword Icon */}
                  <div className={cn(
                    "w-14 h-14 rounded-xl flex items-center justify-center text-3xl",
                    isUnlocked ? "bg-black/20" : "bg-black/10"
                  )}>
                    {isUnlocked ? sword.emoji : <Lock className="w-6 h-6 text-slate-500" />}
                  </div>
                  
                  {/* Sword Info */}
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "font-bold",
                        isUnlocked ? "text-white" : "text-slate-500"
                      )}>
                        {sword.name}
                      </span>
                      {isBest && isUnlocked && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-300 text-xs font-bold rounded-full">
                          BEST
                        </span>
                      )}
                    </div>
                    <p className={cn(
                      "text-sm",
                      isUnlocked ? "text-white/70" : "text-slate-600"
                    )}>
                      {isUnlocked ? sword.description : `Unlock at Level ${sword.unlockLevel}`}
                    </p>
                  </div>
                  
                  {/* Size Badge */}
                  <div className={cn(
                    "px-3 py-1 rounded-lg text-sm font-bold",
                    isUnlocked ? "bg-black/20 text-white" : "bg-black/10 text-slate-600"
                  )}>
                    {sword.size}x{sword.size}
                  </div>
                  
                  {/* Selection Indicator */}
                  {isEquipped && isUnlocked && (
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Footer - Uses info */}
          <div className="p-4 border-t border-white/10 bg-slate-800/50">
            <div className="flex justify-between items-center">
              <p className="text-slate-400 text-sm">
                💡 Standard swords: <span className="text-white font-bold">{swordUsesRemaining}/{maxSwordUses}</span> uses left
              </p>
              {hasGodSword && (
                <p className="text-slate-400 text-sm">
                  ⚔️ God Sword: <span className="text-yellow-400 font-bold">{godSwordUsesRemaining}/5</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Shimmer animation style */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </>
  );
}

export default SwordSelectionModal;
